<script setup lang="ts">
/**
 *
 * @name CertificatesModal
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE QUE MUESTRA UN MODAL CON LA LISTA DE CARRERAS Y PERMITE DESCARGAR PLANTILLAS DE CERTIFICADOS
 *
 * responsabilidades:
 * R1: PERMITE VISUALIZAR LA LISTA DE CARRERAS DISPONIBLES
 * R2: PERMITE VER LOS PLANES DE ESTUDIO DE CADA CARRERA
 * R3: PERMITE DESCARGAR PLANTILLAS DE CERTIFICADOS
 * R4: PERMITE FILTRAR CARRERAS VÁLIDAS
 * R5: PERMITE OBTENER EL NIVEL DE ESTUDIOS DE UNA CARRERA
 *
 * @changelog 1.0.0 - 2025-06-17 - @Jorel254 - Versión inicial del componente
 */

import { ICareerResponse, EIdNivelEstudios, CIdNivelEstudios, ETipoCarrera, NOMBRE_TIPO_CARRERA } from '@shared/types'
import { certificateService } from '@/services/certificateService'
import { useStore } from 'vuex'
import { ref, computed, onMounted } from 'vue'
import { getInstitutionConfiguration } from '@/components/utils/functions'
import BaseModal from '../BaseModal.vue'
/**
 * @description Props del componente
 * @property {string} title - Título del modal
 * @property {ICareerResponse[]} carreras - Lista de carreras disponibles
 * @property {boolean} modelValue - Controla la visibilidad del modal
 */
const props = defineProps<{
	title: string
	carreras: ICareerResponse[]
	modelValue: boolean
	institutionId: string
}>()

/**
 * @description Emits del componente
 * @param {string} update:modelValue - Emitido cuando cambia la visibilidad del modal
 */
const emit = defineEmits<(e: 'update:modelValue', value: boolean) => void>()
const errorMessage = ref<string>('')
const showErrorModal = ref(false)
/**
 * @description Computed property que controla la visibilidad del modal
 * @type {ComputedRef<boolean>}
 */
const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})

/**
 * @description Store de Vuex
 */
const store = useStore()

/**
 * @description Computed property que filtra las carreras válidas
 * @type {ComputedRef<ICareerResponse[]>}
 */
const carreras = computed(() => props.carreras.filter(carrera => carrera !== undefined && carrera !== null))

const conectorCarreraNivel = ref<string>('')

// Load institution configuration on mount
onMounted(async () => {
	try {
		conectorCarreraNivel.value = (await getInstitutionConfiguration(props.institutionId)).degree?.conectorCarreraNivel || ''
	} catch (error) {
		console.error('Error loading institution configuration:', error)
	}
})
/**
 * @description Función que obtiene el nivel de estudios
 * @param {string | number} idNivelEstudios - ID del nivel de estudios
 * @returns {string} Nombre del nivel de estudios
 */
const getNivelEstudios = (idNivelEstudios: string | number) => EIdNivelEstudios[idNivelEstudios as keyof typeof EIdNivelEstudios] || ''

/**
 * @description Función que descarga la plantilla de un certificado
 * @param {string} idCarrera - ID de la carrera
 * @param {string} idRevoe - ID del REVOE
 * @param {string} indexPlan - Clave del plan de estudios
 * @param {string} nombrePlan - Nombre del plan de estudios
 * @param {string} institutionId - ID de la institución
 * @async
 * @returns {Promise<void>}
 */
const downloadTemplate = async (idCarrera: string, idRevoe: string, indexPlan: string, nombrePlan: string, institutionId: string) => {
	try {
		showModal.value = false
		await certificateService.downloadTemplate(idCarrera, idRevoe, indexPlan, nombrePlan, institutionId)
	} catch (error) {
		console.error('Error downloading template:', error)
		errorMessage.value = 'Error al descargar la plantilla'
		showErrorModal.value = true
	}
}

/**
 * @description Computed property que obtiene el ID de la institución actual
 * @type {ComputedRef<string>}
 */
const institutionId = computed(() => store.getters['global/currentInstitution'])
</script>
<template>
	<VaModal blur :title="props.title" hideDefaultActions v-model="showModal">
		<VaCard>
			<VaCardContent>
				<VaList>
					<VaListItem v-for="(carrera, index) in carreras" :key="index">
						<VaListItemSection v-for="revoe in carrera.Revoes" :key="revoe.idAutorizacionReconocimiento">
							<div
								v-for="plan in revoe.Planes"
								:key="plan.clavePlan"
								@click="downloadTemplate(carrera.idCarrera.toString(), revoe.numero, plan.clavePlan, institutionId, `${carrera.nombreCarrera}_${revoe.idAutorizacionReconocimiento}_${plan.clavePlan}`)"
								class="career-item">
								<VaListItemLabel class="career-title">
									{{ CIdNivelEstudios[getNivelEstudios(carrera.idNivelEstudios)].toUpperCase() }}{{ ' ' + (carrera.conectorCarreraNivel || conectorCarreraNivel).toUpperCase() || ' EN ' }}
									{{ carrera.nombreCarrera.toLocaleUpperCase() }}
								</VaListItemLabel>
								<VaListItemLabel caption class="career-subtitle"> Plan: {{ plan.clavePlan }} </VaListItemLabel>
								<VaListItemLabel caption class="career-subtitle"> Tipo de carrera: {{ NOMBRE_TIPO_CARRERA[plan.tipoCarrera as ETipoCarrera] || 'Sin Tipo de Carrera' }} </VaListItemLabel>
							</div>
						</VaListItemSection>
					</VaListItem>
				</VaList>
			</VaCardContent>
		</VaCard>
	</VaModal>

	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.career-item {
	padding: 10px;
	cursor: pointer;
	transition: background-color 0.2s;
}

.career-item:hover {
	background-color: rgba(0, 0, 0, 0.05);
}

.career-title {
	font-weight: 500;
	margin-bottom: 4px;
}

.career-subtitle {
	color: #666;
	font-size: 0.9em;
}
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
</style>
